import styled from '@emotion/styled';
import { useRequest } from 'ahooks';
import { Button, Checkbox, message, Modal, Tag } from 'antd';
import React from 'react';
import { useTranslate } from '@tolgee/react';
import { getBlockListConfig, updateBlockListConfig } from 'services/api/blockList';
import { useSetBlockListConfig } from 'hooks/business/useBlockListConfig';
import TagInput from './TagInput';
import { useOfficialReasonList } from './useOfficialReason';

interface Props {
  visible: boolean;
  onClose: () => void;
}

const Container = styled.div`
  .label {
    margin-top: 24px;
    color: #232426;
    margin-bottom: 8px;
    &-mt-0 {
      margin-top: 0;
    }
  }
  .scope-select {
    background-color: #f0f2f5;
    padding: 4px;
    display: inline-block;
    .ant-btn {
      color: #336fff;
    }
    .ant-btn-text {
      color: #4e5969;
    }
  }
  .reason {
    border: none;
    height: 32px;
    line-height: 32px;
    &.official {
      color: #00bf69;
      background-color: #d7f5e8;
    }
    & + & {
      margin-right: 16px;
    }
  }
  .checkbox {
    color: #515359;
  }
`;

const BlockListSettingModal = React.memo((props: Props) => {
  const { visible, onClose } = props;
  const { t } = useTranslate();
  const setConfig = useSetBlockListConfig();

  const { data, run, mutate } = useRequest(async () => {
    const ret = await getBlockListConfig();
    setConfig((pre) => {
      return {
        ...pre,
        ...ret,
      };
    });
    return ret;
  });
  const onSubmit = () => {
    updateBlockListConfig({
      ...data,
      reasons: undefined,
    })
      .then(() => {
        run();
        onClose();
        message.success(t('api.common.success.messages', { action: t('api.common.action.save') }));
      })
      .catch(() =>
        message.error(t('api.common.error.messages', { action: t('api.common.action.save') })),
      );
  };

  const officialReasonList = useOfficialReasonList();

  if (!data) {
    return null;
  }

  return (
    <Modal
      title={t('ViewBlockList.setting')}
      visible={visible}
      onCancel={onClose}
      okText={t('ViewBlockList.确认设置')}
      cancelText={t('ViewBlockList.暂不设置')}
      maskClosable={false}
      onOk={onSubmit}
    >
      <Container>
        <div className="label label-mt-0">{t('ViewBlockList.拉黑买家默认生效范围')}</div>
        <div className="scope-select">
          <Button
            type={data?.blacklistScope !== 2 ? 'default' : 'text'}
            onClick={() => {
              mutate({ ...data, blacklistScope: 1 });
            }}
          >
            {t('ViewBlockList.全部店铺')}
          </Button>
          <Button
            type={data?.blacklistScope === 2 ? 'default' : 'text'}
            onClick={() => {
              mutate({ ...data, blacklistScope: 2 });
            }}
          >
            {t('ViewBlockList.仅当前店铺')}
          </Button>
        </div>
        <div className="label">{t('ViewBlockList.拉黑原因')}</div>
        <div>
          {officialReasonList.map((x) => (
            <Tag className="reason official">{x}</Tag>
          ))}
        </div>
        <div className="custom-reason-list">
          <TagInput
            tags={data.customReasons || []}
            officialTags={officialReasonList}
            onChange={(tags) => {
              mutate({
                ...data,
                customReasons: tags,
              });
            }}
          />
        </div>
        <div className="label">{t('ViewBlockList.以下买家自动拉黑')}</div>
        <div>
          <Checkbox
            checked={data.autoBlacklistRule?.includes?.(1)}
            className="checkbox"
            onChange={(e) => {
              const check = e.target.checked;
              const rule = data.autoBlacklistRule || [];
              if (check) {
                mutate({
                  ...data,
                  autoBlacklistRule: [...rule, 1],
                });
              } else {
                mutate({
                  ...data,
                  autoBlacklistRule: data.autoBlacklistRule?.filter((x) => x !== 1),
                });
              }
            }}
          >
            {t('ViewBlockList.买家发送优惠卡片')}
          </Checkbox>
          <Checkbox
            checked={data.autoBlacklistRule?.includes?.(2)}
            className="checkbox"
            onChange={(e) => {
              const check = e.target.checked;
              const rule = data.autoBlacklistRule || [];
              if (check) {
                mutate({
                  ...data,
                  autoBlacklistRule: [...rule, 2],
                });
              } else {
                mutate({
                  ...data,
                  autoBlacklistRule: data.autoBlacklistRule?.filter((x) => x !== 2),
                });
              }
            }}
          >
            {t('ViewBlockList.买家拉黑店铺')}
          </Checkbox>
        </div>
      </Container>
    </Modal>
  );
});

export default BlockListSettingModal;
